import { transformUrl } from "unpic"

export type LoaderStatus =
  | "optimized"
  | "no-lever"
  | "declined-on-cost"
  | "broken-data"
  | "acceptable"

export interface ImageLoaderEntry {
  id: string
  label: string
  match: (src: string) => boolean
  transform?: (src: string) => string
  loader: (src: string, width: number, quality?: string, format?: string) => string
  // Present when unpic does the work and the entry exists only to record which tenants ride on it —
  // without it these get listed as loaders we wrote, and duplicated against unpic's own provider list.
  via?: "unpic"
  lever: "query" | "path" | "suffix" | "none"
  honours: string[]
  suppressAvif?: boolean
  status: LoaderStatus
  cause?: string
  owner?: "ingestion" | "platform" | "account" | "hakio"
  remedy?: string
  tenants: string[]
}

export const defaultWidths = [32, 64, 128, 256]

export const getClosestWidth = (targetWidth: number) => {
  return defaultWidths.reduce((prev, curr) => {
    return Math.abs(curr - targetWidth) < Math.abs(prev - targetWidth) ? curr : prev
  })
}

export const ensureHttps = (url: string): string => {
  if (!url) return url

  // Hash fragments are client-side only; CDNs don't use them
  const cleanUrl = url.split("#")[0] ?? url

  // Don't modify data URLs, SVGs, or relative paths
  if (cleanUrl.startsWith("data:") || cleanUrl.startsWith("/") || cleanUrl.startsWith("#")) {
    return cleanUrl
  }

  // Replace http:// with https://
  if (cleanUrl.startsWith("http://")) {
    return cleanUrl.replace("http://", "https://")
  }

  // If the URL has no protocol (e.g., "example.com/image.jpg"), add https://
  if (!cleanUrl.includes("://")) {
    return `https://${cleanUrl}`
  }

  return cleanUrl
}

function defaultLoader(src: string, width: number, quality = "75", format?: string) {
  const hasParams = src.includes("?")
  const paramJoin = hasParams ? "&" : "?"
  const formatParam = format ? `&fm=${format}` : ""
  return `${src}${paramJoin}w=${width}&q=${quality}${formatParam}`
}

function unpicLoader(src: string, width: number, quality = "75", format?: string): string {
  const result = transformUrl({
    url: src,
    width,
    quality: Number(quality),
    format: format as "webp" | "avif" | undefined,
  })
  return result ?? defaultLoader(src, width, quality, format)
}

function fastlyLoader(src: string, width: number) {
  return `${src}${src.includes("?") ? "&" : "?"}width=${width}`
}

// commercetools' derived sizes, by the width each one delivers.
const commercetoolsSizes = [
  [37, "-thumb"],
  [112, "-small"],
  [300, "-medium"],
  [525, "-large"],
] as const

function commercetoolsLoader(src: string, width: number) {
  const suffix = commercetoolsSizes.find(([w]) => width <= w)?.[1] ?? "-zoom"
  return src.replace(/(\.[a-z0-9]+)(\?|$)/i, `${suffix}$1$2`)
}

function meyLoader(src: string, width: number) {
  const url = new URL(src)
  const declaredWidth = Number(url.searchParams.get("width"))
  const declaredHeight = Number(url.searchParams.get("height"))
  if (!declaredWidth || !declaredHeight) return src
  url.searchParams.set("width", String(width))
  url.searchParams.set("height", String(Math.round((declaredHeight / declaredWidth) * width)))
  return url.toString()
}

function colectLoader(src: string, width: number) {
  // Never ask for more than the ingested URL already did — colect upscales past its own
  // source, which costs bytes without adding detail.
  return src.replace(/\/resize\/(\d+)\/\d+\//, (segment, ingested: string) =>
    width >= Number(ingested) ? segment : `/resize/${width}/${width}/`,
  )
}

// Cloudinary custom domains read transforms from the path and ignore query params. The ingested
// transform pins a height, so drop it and size by width, leaving f_auto to choose the format.
function cloudinaryPathLoader(src: string, width: number) {
  return src.replace(/(\/product\/)([^/]+)/, (segment, prefix: string, transform: string) => {
    const parts = transform.split(",")
    if (!parts.some((part) => /^[hw]_\d+$/.test(part))) return segment
    return `${prefix}${[...parts.filter((part) => !/^[hw]_\d+$/.test(part)), `w_${width}`].join(",")}`
  })
}

function amplienceLoader(src: string, width: number, quality = "75", format = "auto") {
  // https://amplience.com/developers/docs/dev-tools/guides-tutorials/media-delivery/smart-image/
  const hasParams = src.includes("?")
  const paramJoin = hasParams ? "&" : "?"
  const formatParam = format ? `&fmt=${format}` : ""
  return `${src}${paramJoin}w=${width}&h=${width}&q=${quality}${formatParam}`
}

function itsperfectLoader(src: string, width: number) {
  // itsperfect ignores query params entirely, so the only lever is the size segment in the path:
  // /producten/thumb/ is 167x250 against /producten/max/'s ~1750x2600 — 50x the bytes and 110x the
  // pixels to decode for a 32px cell. Params are dropped too, so every srcSet candidate collapses
  // onto the same two URLs instead of one uncacheable URL per format/width.
  return width <= 128 ? src.replace("/producten/max/", "/producten/thumb/") : src
}

// Per-tenant hosts, measured sizes and the probe protocol for adding one:
// frontend_api/docs/IMAGE.md. Take the tenant list from BigQuery (`hakio_meta.companies`)
// and sample URLs from frontend_api/data/metadata/<org>/samples/Sku.jsonl rather than a
// list kept here — the hardcoded one covered 20 tenants of 30 and hid ten broken hosts.
export const IMAGE_LOADERS: ImageLoaderEntry[] = [
  {
    id: "amplience",
    label: "Amplience",
    match: (src) => src.includes("amplience"),
    // Strip Amplience preset templates (e.g. ?$haiko_st$) before adding our params
    transform: (src) => src.replace(/\?\$[^&]+\$/, ""),
    loader: amplienceLoader,
    lever: "query",
    honours: ["width", "height", "quality", "format"],
    status: "optimized",
    tenants: ["carhartt_wip"],
  },
  {
    id: "kontainer",
    label: "Kontainer",
    match: (src) => src.includes(".kontainer.com"),
    // d= is a Kontainer composition preset that offsets the product onto a large canvas.
    // Strip it so we get a plain product image, then apply our resize params normally.
    transform: (src) => src.replace(/[?&]d=[^&]+/, "").replace(/\?$/, ""),
    loader: defaultLoader,
    lever: "query",
    honours: ["width", "quality", "format"],
    status: "optimized",
    tenants: ["les_deux"],
  },
  {
    id: "itsperfect",
    label: "itsperfect",
    match: (src) => src.includes(".itsperfect.it"),
    loader: itsperfectLoader,
    lever: "path",
    honours: [],
    status: "optimized",
    tenants: ["by_bar"],
  },
  {
    id: "bunny",
    label: "Bunny (Contentful proxy)",
    match: (src) => src.includes("b-cdn.net"),
    // Bunny pull-zone proxying the Contentful Images API: unpic would emit Bunny
    // params (width/quality/output) that Contentful rejects with 400.
    // Contentful uses w/q/fm — exactly defaultLoader's scheme.
    loader: defaultLoader,
    lever: "query",
    honours: ["width", "quality", "format"],
    status: "optimized",
    tenants: ["tekla"],
  },
  {
    id: "fastly-strauss",
    label: "Fastly Image Optimizer",
    match: (src) => src.includes("cdn.strauss.com"),
    // Fastly Image Optimizer: width is honoured, quality and format are not — it picks
    // webp off Accept by itself. Emitting width alone keeps the srcSet at four URLs.
    loader: fastlyLoader,
    lever: "query",
    honours: ["width"],
    status: "optimized",
    tenants: ["strauss"],
  },
  {
    id: "commercetools",
    label: "commercetools",
    match: (src) => src.includes(".commercetools.com"),
    // commercetools ignores query params and publishes a fixed ladder of derived sizes
    // as filename suffixes instead: -thumb is 37x50 against a 2405x3209 original.
    loader: commercetoolsLoader,
    lever: "suffix",
    honours: [],
    status: "optimized",
    tenants: ["palmers"],
  },
  {
    id: "mey",
    label: "mey",
    match: (src) => src.includes("media.meyb2b.com"),
    // The ingested URL already carries mey's own width/height, so overwrite those rather
    // than appending ours. Both are required together — width alone serves the original.
    loader: meyLoader,
    lever: "query",
    honours: ["width", "height"],
    status: "optimized",
    tenants: ["mey"],
  },
  {
    id: "colect",
    label: "colect",
    match: (src) => src.includes("images.colect.services"),
    // colect sizes through the path and ignores query params.
    loader: colectLoader,
    lever: "path",
    honours: [],
    status: "optimized",
    tenants: ["startex"],
  },
  {
    id: "shopify",
    label: "Shopify",
    match: (src) => src.includes("cdn.shopify.com"),
    loader: unpicLoader,
    via: "unpic",
    lever: "query",
    honours: ["width", "quality", "format"],
    status: "optimized",
    tenants: [
      "winther_studio",
      "teveo",
      "shaping_new_tomorrow",
      "suit_club",
      "laagam",
      "mayafreya",
      "pitaya",
      "splendid",
    ],
  },
  {
    id: "cloudinary",
    label: "Cloudinary",
    match: (src) => src.includes("cloudinary.com"),
    loader: unpicLoader,
    via: "unpic",
    lever: "query",
    honours: ["width", "quality", "format"],
    status: "optimized",
    tenants: ["organic_basics"],
  },
  {
    id: "sanity",
    label: "Sanity",
    match: (src) => src.includes("cdn.sanity.io"),
    // Sanity (an imgix fork) rejects fm=avif with a 400; it only delivers AVIF via auto=format.
    loader: unpicLoader,
    via: "unpic",
    lever: "query",
    honours: ["width", "quality", "format"],
    suppressAvif: true,
    status: "optimized",
    tenants: ["marimekko"],
  },
  {
    id: "scene7",
    label: "Scene7 (Adobe DM)",
    match: (src) => src.includes("/is/image"),
    loader: unpicLoader,
    via: "unpic",
    lever: "query",
    honours: ["width", "quality", "format"],
    status: "optimized",
    tenants: ["pentland"],
  },
  {
    id: "spysystem",
    label: "SPY System",
    match: (src) => src.includes("spysystem.dk"),
    loader: unpicLoader,
    lever: "none",
    honours: [],
    status: "no-lever",
    cause: "Origin returns the original byte-for-byte for every param and path shape tried.",
    owner: "platform",
    remedy: "Add a resizing proxy (e.g. a Bunny pull-zone) in front of the origin.",
    tenants: ["colorful_standard", "knowledgecotton_apparel", "redefined_fashion"],
  },
  {
    id: "ball-group",
    label: "Ball Group PIM",
    match: (src) => src.includes("pim.ball-group.com"),
    loader: unpicLoader,
    lever: "none",
    honours: [],
    status: "no-lever",
    cause:
      "Origin returns the original byte-for-byte for every param and path shape tried; the 2500x2500 in the filename is part of the name, not a size selector.",
    owner: "platform",
    remedy: "Add a resizing proxy in front of the origin.",
    tenants: ["zizzi"],
  },
  {
    id: "agent-provocateur",
    label: "Agent Provocateur",
    match: (src) => src.includes("agentprovocateur.com"),
    loader: unpicLoader,
    lever: "none",
    honours: [],
    status: "no-lever",
    cause: "Cloudflare zone in front of the origin, but image resizing is not enabled on it.",
    owner: "account",
    remedy: "Get the customer to enable Cloudflare image resizing on their zone.",
    tenants: ["agent_provocateur"],
  },
  {
    id: "samsoe-blob",
    label: "Samsøe Samsøe Azure Blob",
    match: (src) => src.includes("samsoeedwstorage.blob.core.windows.net"),
    loader: unpicLoader,
    lever: "none",
    honours: [],
    status: "no-lever",
    cause: "Azure Blob Storage + SAS token, no transform layer in front of it.",
    owner: "platform",
    remedy: "Add a resizing proxy in front of the origin.",
    tenants: ["samsoe_samsoe"],
  },
  {
    id: "tekla-s3",
    label: "tekla Medusa S3 (secondary host)",
    match: (src) => src.includes("s3.eu-central-1.amazonaws.com"),
    loader: unpicLoader,
    lever: "none",
    honours: [],
    status: "no-lever",
    cause: "Medusa S3 bucket that bypasses tekla's Bunny pull-zone entirely.",
    owner: "ingestion",
    remedy: "Route these assets through tekla's existing Bunny pull-zone instead.",
    tenants: ["tekla"],
  },
  {
    id: "hummel",
    label: "hummel Digizuite",
    match: (src) => src.includes("dam.hummel.dk"),
    loader: unpicLoader,
    lever: "none",
    honours: [],
    status: "declined-on-cost",
    cause:
      "Digizuite drops our query params. The Cloudflare zone in front of it does have image resizing enabled, but it bills hummel per unique transformation on their own account.",
    owner: "account",
    remedy:
      "Get hummel's sign-off before requesting /cdn-cgi/image/width=…,quality=…,format=… in front of the origin.",
    tenants: ["hummel"],
  },
  {
    id: "g-star",
    label: "G-Star (Cloudinary custom domain)",
    match: (src) => src.includes("img1.g-star.com"),
    loader: cloudinaryPathLoader,
    lever: "path",
    honours: ["width"],
    status: "broken-data",
    cause:
      "Individual colourway/size codes are stale — 25 of a 41-URL spread 404. The transform prefix is not: it is byte-identical on the assets that do serve, and the path lever sizes those correctly. A stale code renders the placeholder regardless.",
    owner: "ingestion",
    remedy: "Fix the stale codes. No loader can help a code that 404s.",
    tenants: ["g_star"],
  },
  {
    id: "applied-art-forms",
    label: "Applied Art Forms (Cloudinary custom domain, g_star secondary host)",
    match: (src) => src.includes("img1.appliedartforms.com"),
    loader: cloudinaryPathLoader,
    lever: "path",
    honours: ["width"],
    status: "optimized",
    tenants: ["g_star"],
  },
  {
    id: "insight-cosmetics",
    label: "insight_cosmetics",
    match: (src) => src.includes("magento.s3") || src.includes("icgroup.dk"),
    loader: unpicLoader,
    lever: "none",
    honours: [],
    status: "broken-data",
    cause:
      "magento.s3… keys 404, and many rows store an HTML search-page URL (icgroup.dk) instead of an image.",
    owner: "ingestion",
    remedy:
      "Fix the ingestion mapping so image_url points at a real asset, not the search-page fallback.",
    tenants: ["insight_cosmetics"],
  },
  {
    id: "mos-mosh-sentinel",
    label: "mos_mosh placeholder GUID",
    match: (src) => src.includes("00000000-0000-0000-0000-000000000000"),
    loader: unpicLoader,
    lever: "none",
    honours: [],
    status: "broken-data",
    cause:
      "imageUrl is the literal placeholder GUID for 100% of SKUs; ensureHttps turns it into an unresolvable host.",
    owner: "ingestion",
    remedy: "Fix the ingestion mapping to source a real image URL instead of the placeholder GUID.",
    tenants: ["mos_mosh"],
  },
  {
    id: "fiberpartner",
    label: "fiberpartner (WordPress)",
    match: (src) => src.includes("fiberpartner.com"),
    loader: unpicLoader,
    lever: "none",
    honours: [],
    status: "acceptable",
    cause:
      "Not a real product image — the ingestion view hardcodes one literal URL for every SKU, so every row shares a single small cached fetch.",
    tenants: ["fiberpartner"],
  },
  {
    id: "les-deux-thumbnail",
    label: "les_deux thumbnail blob (secondary host)",
    match: (src) => src.includes("mydprdst1.blob.core.windows.net"),
    loader: unpicLoader,
    lever: "none",
    honours: [],
    status: "acceptable",
    cause:
      "Already a thumbnail (298x298); the non-_thumbnail name 404s so there's no larger variant to shrink.",
    tenants: ["les_deux"],
  },
]

const UNPIC_FALLBACK: ImageLoaderEntry = {
  id: "unpic",
  label: "Unpic (auto-detected CDN)",
  match: () => false,
  loader: unpicLoader,
  lever: "query",
  honours: ["width", "quality", "format"],
  status: "optimized",
  tenants: [],
}

export function resolveLoader(src: string): ImageLoaderEntry {
  return IMAGE_LOADERS.find((entry) => entry.match(src)) ?? UNPIC_FALLBACK
}
